/**
 * Master card queue - all cards, ordered by priority.
 * The backend (userNotificationStates) decides which cards apply to a given
 * user — cards judged not_applicable are simply absent from the results — and
 * computes each card's status (queued/started/completed/etc). The UI only
 * decides display order/priority (the order of this array) and still owns
 * the visibility gating for the 2 statsig-flagged cards and the "blocked"
 * (disabled) state, which is derived from the returned data.
 *
 * New cards defined by the backend are ignored unless they are defined in
 * this array. It is therefore safe for the backend to add cards that support
 * a UI feature branch before the feature branch is merged.
 *
 * This order is the fallback used when the user has no ctaSegment; with one,
 * orderCardDefs applies CTA_SEGMENT_ORDERS instead. It matches the UX tables
 * for every segment except two pairs that one global order can't satisfy:
 *   - Combo Active wants profile-standout before find-offer; Educator Active
 *     (favored here) wants the reverse.
 *   - Educator Inactive wants build-community before get-involved-partner;
 *     Educator Unfunded (favored here) wants the reverse.
 *
 * Card def shape:
 *   id, color, title, description, ctaLabel, successCtaLabel,
 *   successLabel, inProgressLabel, linkDestination, iconName,
 *   isBlocked(statesByKey), isVisible()
 * Omit any fn to get the default (false / visible).
 */
export function getAllCardDefs(accountSetup) {
  const isCompleted = (statesByKey, id) => statesByKey[id]?.status === 'completed';
  const profileDone = (statesByKey) => isCompleted(statesByKey, 'cta:setup-profile');

  return [
    // -- Set up your public profile (everyone)
    // On the first login — shown always until complete; blocks all other cards.
    {
      id: 'cta:setup-profile',
      color: 'blue',
      title: 'Set up your public profile',
      description: "Share a little about yourself so others can understand how you'd like to collaborate.",
      ctaLabel: 'Set up profile',
      successCtaLabel: 'View profile',
      successLabel: 'Completed',
      inProgressLabel: 'Almost done',
      linkDestination: (statesByKey) => profileDone(statesByKey) ?
        '/profile/view' :
        'dialog:ProfileEdit',
      iconName: 'PublicProfile',
    },
    // Start the setup process for your site
    {
      id: 'cta:site-setup',
      color: 'blue',
      title: 'Start the setup process for your site',
      description: 'Answer a few questions, and our team will reach out to help you move forward.',
      ctaLabel: 'Start setup',
      inProgressCtaLabel: 'N/A',
      inProgressLabel: 'Almost done',
      successCtaLabel: 'N/A',
      successLabel: 'Submitted',
      linkDestination: (statesByKey) => isCompleted(statesByKey, 'cta:site-setup') ?
        null :
        'dialog:SiteMissing',
      iconName: 'Site',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Create a project or event (educator-active-site)
    {
      id: 'cta:create-project',
      color: 'orange',
      title: 'Create a project or event for your learners',
      description: "Share your idea, and we'll connect you to partners who can help you bring it to life!",
      ctaLabel: 'Create project or event',
      successCtaLabel: 'View project or event',
      successLabel: 'Completed',
      inProgressLabel: 'Draft in progress',
      linkDestination: (statesByKey) => statesByKey['cta:create-project']?.status === 'started' ?
        '/my-leading#draft-leading' :
        'dialog:SelectorProjectEventOffer',
      iconName: 'CreateProject',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Share a project
    {
      id: 'cta:share-project',
      color: 'blue',
      title: 'Share your project with your community!',
      description: "Tell your community what you're working on. Great partnerships often start with a personal recommendation.",
      ctaLabel: 'Share project',
      successCtaLabel: 'View projects',
      successLabel: 'Shared',
      inProgressLabel: 'N/A',
      linkDestination: 'dialog:ProjectShare',
      iconName: 'Partner',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Get involved in an existing project
    {
      id: 'cta:get-involved-project',
      color: 'yellow',
      title: 'Get involved in an existing project',
      description: 'Browse active projects and connect with one that feels like a good fit',
      ctaLabel: 'Browse projects',
      successCtaLabel: 'N/A',
      successLabel: 'Connected',
      linkDestination: '/projects',
      iconName: 'GetInvolved',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Get involved virtually
    {
      id: 'cta:get-involved-virtual',
      color: 'yellow',
      title: 'Get involved in a project virtually',
      description: 'Browse projects where educators are looking for virtual partners to help bring learning to life',
      ctaLabel: 'Get involved',
      successCtaLabel: 'N/A',
      successLabel: 'Connected',
      linkDestination: '/projects?interactionTypes=526',
      iconName: 'GetInvolved',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Start contributing as a partner
    {
      id: 'cta:start-contributing',
      color: 'yellow',
      title: 'Start contributing as a partner',
      description: 'Share your skills by supporting an educator\'s project or creating an offer of your own.',
      ctaLabel: 'Start contributing',
      successCtaLabel: 'N/A',
      successLabel: 'Completed',
      inProgressLabel: 'Almost done',
      linkDestination: '/my-opportunities',
      iconName: 'StartContributing',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Find a partner offer
    {
      id: 'cta:find-offer',
      color: 'yellow',
      title: 'Find a partner offer that fits your interests',
      description: 'Explore opportunities and find ones that support your goals and engage your learners.',
      ctaLabel: 'View offers',
      successCtaLabel: 'N/A',
      successLabel: 'Connected',
      linkDestination: '/offers',
      iconName: 'CreateOffer',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Create an offer
    {
      id: 'cta:create-offer-active',
      color: 'orange',
      title: 'Create an offer',
      description: "Post an opportunity — like a workshop, internship, or site visit — for educators and learners to connect with.",
      ctaLabel: 'Create offer',
      inProgressCtaLabel: 'Continue offer',
      successCtaLabel: 'View offer',
      successLabel: 'Completed',
      inProgressLabel: 'Draft in progress',
      linkDestination: (statesByKey) => statesByKey['cta:create-offer-active']?.status === 'started' ?
        '/my-leading#draft-leading' :
        '/post-offer',
      iconName: 'CreateOffer',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Create an offer (virtual)
    {
      id: 'cta:create-offer-virtual',
      color: 'orange',
      title: 'Create an offer (virtual)',
      description: "Post a virtual opportunity — like a workshop or internship — for educators and learners to connect with.",
      ctaLabel: 'Create offer',
      inProgressCtaLabel: 'Continue offer',
      successCtaLabel: 'View offer',
      successLabel: 'Completed',
      inProgressLabel: 'Draft in progress',
      linkDestination: (statesByKey) => statesByKey['cta:create-offer-virtual']?.status === 'started' ?
        '/my-leading#draft-leading' :
        '/post-offer',
      iconName: 'CreateOffer',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Get involved as a community partner -- retired
    {
      id: 'cta:get-involved-partner',
      color: 'blue',
      title: 'Get involved as a community partner',
      description: 'You don\'t have to wait — you can start sharing your skills with learners and educators right away.',
      ctaLabel: 'Get involved',
      successCtaLabel: 'N/A',
      successLabel: 'Connected',
      linkDestination: '/projects',
      iconName: 'Partner',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Make your profile stand out
    {
      id: 'cta:profile-standout',
      color: 'blue',
      title: 'Make your profile stand out',
      description: 'A few extra details helps us connect you with the right partners for your project',
      ctaLabel: 'Add profile information',
      successCtaLabel: 'View profile',
      successLabel: 'Completed',
      inProgressLabel: 'Almost done',
      linkDestination: '/profile/edit',
      iconName: 'Polish',
      neverEmphasize: true,
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Evaluate your project
    {
      id: 'cta:evaluate-project',
      color: 'blue',
      title: 'Evaluate your project',
      description: "Help us learn more about the impact that your project had on your learners.",
      ctaLabel: 'Start evaluation',
      successCtaLabel: 'N/A',
      successLabel: 'Evaluation completed',
      inProgressLabel: 'Almost done',
      linkDestination: '/my-evaluations',
      iconName: 'Evaluate',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
    // -- Sign up for fellowship training
    // Special action — only shown during the fellowship window.
    {
      id: 'cta:fellowship-training',
      color: 'blue',
      title: 'Sign up for the fellowship training',
      description: 'CommunityShare is hosting training events. Join to deepen your skills.',
      ctaLabel: 'Sign up',
      successCtaLabel: 'N/A',
      successLabel: 'Completed',
      linkDestination: 'https://www.communityshare.org/educator-fellowship/',
      iconName: 'Build',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
      isVisible:  () => accountSetup.isFellowshipCTA
    },
    // -- Complete a feedback survey
    {
      id: 'cta:feedback-survey',
      color: 'blue',
      title: 'Complete a feedback survey',
      description: 'Help us learn more about the impact that your project had on your learners.',
      ctaLabel: 'Start survey',
      inProgressLabel: 'Almost done',
      successLabel: 'Completed',
      linkDestination: 'https://www.communityshare.org/',
      iconName: 'Evaluate',
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
      isVisible:  () => accountSetup.isFeedbackCTA
    },
    // -- Build a local community — gap filler
    {
      id: 'cta:build-community',
      color: 'blue',
      title: 'Build a local community',
      description: 'Invite educators and community members in your area to join and grow your local network',
      ctaLabel: 'Invite others',
      successCtaLabel: 'N/A',
      successLabel: 'Invite sent',
      linkDestination: '/my-network',
      iconName: 'Build',
      neverEmphasize: true,
      gapFiller: true,
      isBlocked:  (statesByKey) => !profileDone(statesByKey),
    },
  ];
}

/**
 * Display order per user segment (currentUser.ctaSegment, computed by the
 * backend), taken from the UX sequencing tables: numbered cards first, then
 * unnumbered cards in the order listed, except that special actions
 * (fellowship, survey) rank above the build-community gap filler and
 * share-project follows create-project. In Educator Inactive build-community
 * is numbered card #3, not a gap filler, so it keeps its place there.
 */
export const CTA_SEGMENT_ORDERS = {
  'educator-active': [
    'cta:setup-profile', 'cta:create-project', 'cta:share-project', 'cta:find-offer',
    'cta:profile-standout', 'cta:evaluate-project', 'cta:fellowship-training',
    'cta:feedback-survey', 'cta:build-community',
  ],
  'educator-unfunded': [
    'cta:setup-profile', 'cta:site-setup', 'cta:get-involved-partner', 'cta:profile-standout',
    'cta:fellowship-training', 'cta:feedback-survey', 'cta:build-community',
  ],
  'educator-inactive': [
    'cta:setup-profile', 'cta:site-setup', 'cta:build-community', 'cta:profile-standout',
    'cta:get-involved-partner', 'cta:fellowship-training', 'cta:feedback-survey',
  ],
  'partner-active': [
    'cta:setup-profile', 'cta:get-involved-project', 'cta:create-offer-active',
    'cta:profile-standout', 'cta:feedback-survey', 'cta:build-community',
  ],
  'partner-inactive': [
    'cta:setup-profile', 'cta:get-involved-virtual', 'cta:create-offer-virtual',
    'cta:profile-standout', 'cta:feedback-survey', 'cta:build-community',
  ],
  'combo-active': [
    'cta:setup-profile', 'cta:create-project', 'cta:share-project', 'cta:start-contributing',
    'cta:profile-standout', 'cta:find-offer', 'cta:evaluate-project',
    'cta:fellowship-training', 'cta:feedback-survey', 'cta:build-community',
  ],
  // Educator + Partner in an inactive network or at an unfunded site
  'combo-inactive': [
    'cta:setup-profile', 'cta:site-setup', 'cta:start-contributing', 'cta:profile-standout',
    'cta:feedback-survey', 'cta:build-community',
  ],
};

/**
 * Orders card defs for the user's segment. Cards the segment lists come first
 * in that order; any others the backend still returns follow in master
 * (getAllCardDefs) order. A missing or unknown segment keeps the master order.
 */
export function orderCardDefs(cardDefs, segment) {
  const order = CTA_SEGMENT_ORDERS[segment];
  if (!order) return cardDefs;

  const rank = (def) => {
    const i = order.indexOf(def.id);
    return i === -1 ? order.length : i;
  };

  // Array.prototype.sort is stable, so unlisted cards keep master order
  return [...cardDefs].sort((a, b) => rank(a) - rank(b));
}

/**
 * Takes card defs plus the userNotificationStates keyed by itemKey, applies
 * state logic, and returns CTACard prop objects. Cards without a matching
 * entry in statesByKey are excluded entirely — the backend has already
 * determined they are not_applicable to this user.
 * Emphasis rule: the first non-disabled, non-success card gets Emphasis.
 */
export function resolveCards(cardDefs, statesByKey) {
  // Only keep cards the backend has provided a state for (applicable to this user)
  // and that pass the UI's own visibility gating (statsig-flagged cards).
  const applicable = cardDefs.filter(def => {
    if (!statesByKey[def.id]) return false;
    return !def.isVisible || def.isVisible();
  });

  const resolved = applicable.map(def => {
    const status = statesByKey[def.id].status;
    const pendingExit = needsExitAnimation(statesByKey[def.id]);
    const blocked  = def.isBlocked ? def.isBlocked(statesByKey) : false;
    const complete = status === 'completed';
    const started  = status === 'started';
    const linkDestination = typeof def.linkDestination === 'function'
      ? def.linkDestination(statesByKey)
      : def.linkDestination;

    let state;
    if (complete)      state = 'success';
    else if (started)  state = 'in-progress';
    else               state = 'default';

    return {
      id:              def.id,
      color:           def.color,
      state,
      emphasized:      false,
      disabled:        blocked,
      title:           def.title,
      description:     def.description,
      ctaLabel:        def.ctaLabel,
      successCtaLabel: def.successCtaLabel,
      successLabel:    def.successLabel,
      inProgressCtaLabel: def.inProgressCtaLabel,
      inProgressLabel: def.inProgressLabel,
      linkDestination,
      iconName:        def.iconName,
      gapFiller:       def.gapFiller,
      neverEmphasize:  def.neverEmphasize,
      // Whether this card still owes the user an exit animation. Exposed on
      // the resolved card so callers don't have to re-derive it from
      // statesByKey (see needsExitAnimation).
      needsExit:       pendingExit,
    };
  });

  // Cards that still owe the user an exit animation (see needsExitAnimation)
  // are sorted first, even though their state is 'success', so callers like
  // getTopCards' pending-exit handling can find them at the front of the
  // array instead of having to re-scan resolvedCards themselves.
  resolved.sort((a, b) => {
    if (a.needsExit === b.needsExit) return 0;
    return a.needsExit ? -1 : 1;
  });

  // Apply emphasis to the first non-disabled, non-success, non-neverEmphasize card
  const emphasisTarget = resolved.find(c => !c.disabled && c.state !== 'success' && !c.neverEmphasize);
  if (emphasisTarget) emphasisTarget.emphasized = true;

  return resolved;
}

/**
 * Takes resolved cards and returns up to maxCards cards for the priority
 * section. How many cards to display is a UI layout decision (owned by
 * StreamlinedLayout), so it's passed in by the caller rather than hard-coded
 * here. Cards are no longer persisted to sessionStorage — the backend
 * (userNotificationStates) now owns all card states, so the top cards are
 * simply recomputed from the current resolved cards on every call.
 */
export function getTopCards(resolvedCards, skipSuccess = true, maxCards = 3) {
  const topCards = [];

  // Fill up to maxCards with eligible cards
  const eligibleCards = skipSuccess
    ? resolvedCards.filter(c => c.state !== 'success')
    : resolvedCards;

  while (topCards.length < maxCards && eligibleCards.length > 0) {
    const card = eligibleCards.shift();
    if (!topCards.find(c => c.id === card.id)) topCards.push(card);
  }

  return topCards;
}

/**
 * Whether a card has completed *after* it was last shown to the user
 * (shownAt is set and predates updatedAt) — i.e. it still owes the user an
 * exit animation before it can be removed for good. This applies both to
 * cards that complete while already displayed, and to cards that arrive
 * already completed on the initial load (e.g. completed in another tab/
 * session) — those still need to be shown briefly and animated out rather
 * than silently skipped.
 */
export function needsExitAnimation(state) {
  if (!state || state.status !== 'completed' || !state.shownAt) return false;
  return new Date(state.shownAt) < new Date(state.updatedAt);
}

/**
 * Finds the next best card to fill a slot vacated by a card that has just
 * animated away (completed + previously shown). Unlike getTopCards' initial
 * selection, an already-completed card is not skipped outright here — but
 * only if it still owes the user an exit animation (needsExit): if e.g. 10
 * cards are completed at once and only the first few are displayed, exiting
 * one of them should be able to pull in the next pending-exit card from the
 * remaining pool (it'll then go through its own brief-show-then-exit flow).
 * A completed card that has already had its exit animation (needsExit is
 * false) must never be pulled in — otherwise it would enter the list only
 * to immediately animate away again.
 */
export function getReplacementCard(resolvedCards, excludeIds) {
  const isEligible = (c) => !excludeIds.includes(c.id)
    && (c.state !== 'success' || c.needsExit);

  const eligibleCards = resolvedCards.filter(isEligible);
  return eligibleCards.length > 0 ? eligibleCards[0] : null;
}
